/* ──────────────────────────────────────────────────────────────────────────────
   Faz 3 — Araçlar Arası Transfer İzleme ve Fark Çözümleme  (spec §9)

   Bu ekranın VARLIK SEBEBİ TRANSIT'tir: orada asılı kalan her koli, birinin
   fiziksel olarak sahip olduğu ama HİÇBİR ARACIN STOĞUNDA GÖRÜNMEYEN maldır.
   Görünmeyen mal er ya da geç envanter farkı olarak patlar → ekran onu görünür
   kılar ve çözdürür.

   Üç blok (prototipteki sıra):
     ① TRANSIT'te asılı bakiye — EN ÜSTTE, KIRMIZI. Gerekçe ZORUNLU 3 aksiyon.
     ② Onay bekleyen transferler — `dispatched` + 4 saati aşanlar (mal TRANSIT'te bekliyor).
     ③ Transfer geçmişi — c360 listesi (useC360List): SUNUCU tarafı sayfalama + sıralama + filtre.

   İRSALİYE KOLONU KRİTİKTİR (§9): basılmamış bir taşıma irsaliyesi = yolda giden
   aracın BELGESİZ mal taşıması. Backoffice bunu görür ve temsilciye hatırlatma yollar.

   ══ VERİ KATMANI: ÜÇ BLOK, İKİ UÇ ══════════════════════════════════════════════
   ③ geçmiş listesi → `GET /api/vehicle-transfers/odata` (OData; proje kuralı: listeler OData).
   Eskiden liste ucu OData DEĞİLDİ → ekran `take=500` çekip ISTEMCIDE süzüyordu; 501. transfer
   hiçbir filtreyle bulunamıyordu. Artık LineCount / Total... / *WaybillPrinted alanları GERÇEK
   KOLON olduğu için ("irsaliyesi basılmamışlar" = belgesiz mal taşıyan araç) SQL'e çevrilebiliyor ve
   istemci-tarafı filtreleme TAMAMEN kalktı.

   ①② blokları BİLİNÇLİ OLARAK REST'te (`GET /api/transit-balances`) kalır: asılı bakiye
   StockTransfer'den değil LEDGER'dan (StockMovement) türetilir — belge alanlarından türetilseydi
   postlama ile belge arasındaki tutarsızlık tam da burada GÖRÜNMEZ olurdu.
   Bu iki blok sayfalanmaz; sayfalanan bir listede "bu sayfada asılı bakiye yok" demek
   "asılı bakiye yok" DEMEK DEĞİLDİR. Blokların ihtiyaç duyduğu temsilci/cari bağı,
   geçmiş sayfasından DEĞİL, id'lerle hedefli tek bir OData isteğiyle çözülür (bkz. ctxById).

   DİKKAT: Depo→depo "Stok Transferi" (/stock-transfer, StockDocumentsListPage) AYRI
   bir ekrandır — farklı belge, farklı yaşam döngüsü, farklı aktör.
   ────────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, toGridSortField, toODataSortField, getExplicitODataTotalCount,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge, RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { C360Table, ColumnChooser, ViewIcon, unwrapLookupItems, useC360List } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const TRANSFERS_ODATA = '/api/vehicle-transfers/odata';
const TRANSIT_API = '/api/transit-balances';
const NOTIFY_API = '/api/notifications/broadcast';

// Spec §4 — 8 durumlu makine. Sunucudaki StockTransferStatuses ile birebir.
export const TRANSFER_STATUS = {
  draft: { label: 'Taslak', tone: 'neutral' },
  requested: { label: 'Talep edildi', tone: 'wait' },
  rejected: { label: 'Reddedildi', tone: 'bad' },
  dispatched: { label: 'Transit', tone: 'wait' },
  confirmed: { label: 'Onaylandı', tone: 'ok' },
  confirmed_with_diff: { label: 'Farklı onay', tone: 'wait' },
  cancelled: { label: 'İptal edildi', tone: 'neutral' },
  expired: { label: 'Süresi doldu', tone: 'neutral' },
};
export const statusLabel = (v) => TRANSFER_STATUS[v]?.label || (v || '-');
export const statusTone = (v) => TRANSFER_STATUS[v]?.tone || 'neutral';

// Mal el değiştirdi → aracın stoğu değişti → TAŞIMA İRSALİYESİ ZORUNLU (§13.10).
// Diğer durumlarda (talep/red/iptal/süre doldu) hiç stok hareketi olmadı → irsaliye gerekmez.
export const WAYBILL_REQUIRED_STATUSES = new Set(['dispatched', 'confirmed', 'confirmed_with_diff']);

// Aynı kural OData cümlesi olarak — "irsaliye gerektiren durumlar".
const WAYBILL_REQUIRED_CLAUSE = `(${[...WAYBILL_REQUIRED_STATUSES].map((v) => `Status eq '${v}'`).join(' or ')})`;
// İrsaliyesiz = stok değiştiren AMA iki fişten en az biri basılmamış transfer (§9, §13.10).
const MISSING_WAYBILL_CLAUSE = `${WAYBILL_REQUIRED_CLAUSE} and (FromWaybillPrinted eq false or ToWaybillPrinted eq false)`;
const PRINTED_WAYBILL_CLAUSE = `${WAYBILL_REQUIRED_CLAUSE} and FromWaybillPrinted eq true and ToWaybillPrinted eq true`;
const NA_WAYBILL_CLAUSE = `not ${WAYBILL_REQUIRED_CLAUSE}`;

export const TRANSIT_ACTIONS = [
  { value: 'writeoff', label: 'Farkı Zayiata Yaz', hint: 'Transit sıfırlanır, hiçbir aracın stoğuna yazılmaz.' },
  { value: 'return_to_sender', label: 'Verenin Stoğuna İade Et', hint: 'Fark veren aracın stoğuna geri döner.' },
  { value: 'add_to_receiver', label: 'Alanın Stoğuna Ekle', hint: 'Fark alan aracın stoğuna eklenir.' },
];

const SHORTAGE_REASONS = {
  stok_yetersiz: 'Stok yetersiz',
  kendi_rutum_icin_gerekli: 'Kendi rutum için gerekli',
  hasarli: 'Hasarlı',
};
const DIFFERENCE_REASONS = {
  eksik_teslim: 'Eksik teslim',
  hasarli: 'Hasarlı',
  skt_kisa: 'SKT kısa',
  fazla_teslim: 'Fazla teslim',
};
const REQUEST_REASONS = {
  stok_tukendi: 'Stok tükendi',
  stok_yetersiz: 'Stok yetersiz',
  musteri_talebi: 'Müşteri talebi',
  diger: 'Diğer',
};
export const shortageReasonLabel = (v) => SHORTAGE_REASONS[v] || (v || '-');
export const differenceReasonLabel = (v) => DIFFERENCE_REASONS[v] || (v || '-');
export const requestReasonLabel = (v) => REQUEST_REASONS[v] || (v || '-');

const DATE_PRESETS = [
  { value: '7', label: 'Son 7 gün' },
  { value: '30', label: 'Son 30 gün' },
  { value: '90', label: 'Son 90 gün' },
  { value: '', label: 'Tümü' },
];

const STATUS_OPTIONS = Object.entries(TRANSFER_STATUS).map(([value, meta]) => ({ value, label: meta.label }));
const DIFFERENCE_OPTIONS = [
  { value: 'true', label: 'Fark var' },
  { value: 'false', label: 'Fark yok' },
];
// İrsaliye üç durumludur: gerekmiyor (stok hareketi yok) / basıldı (İKİ araç da) / basılmadı.
const WAYBILL_OPTIONS = [
  { value: 'printed', label: 'Basıldı' },
  { value: 'missing', label: 'Basılmadı' },
  { value: 'na', label: 'Gerekmiyor' },
];
const WAYBILL_CLAUSE_BY_VALUE = {
  printed: PRINTED_WAYBILL_CLAUSE,
  missing: MISSING_WAYBILL_CLAUSE,
  na: NA_WAYBILL_CLAUSE,
};

/**
 * Kolon filtre hunileri — HEPSİ SUNUCUDA süzülür (c360 filterModel).
 *
 * `waybill` tek bir `field op value` kalıbına sığmaz (durum + iki bayrak) → filterModel'in
 * `buildClause` kaçış kapısı kullanılır. `difference` kanonik `HasDiscrepancy` kolonundan süzülür
 * (`valueFormat: 'raw'` → tırnaksız `eq true`).
 */
const COLUMN_FILTER_META = {
  transferNo: { type: 'text', odataField: 'TransferNo' },
  fromVehicle: { type: 'select', odataField: 'FromWarehouseId', valueFormat: 'guid' },
  toVehicle: { type: 'select', odataField: 'ToWarehouseId', valueFormat: 'guid' },
  vendor: { type: 'select', odataField: 'VendorId', valueFormat: 'guid' },
  lineCount: { type: 'number', odataField: 'LineCount' },
  status: { type: 'select', odataField: 'Status', valueFormat: 'string', options: STATUS_OPTIONS },
  difference: { type: 'select', odataField: 'HasDiscrepancy', valueFormat: 'raw', options: DIFFERENCE_OPTIONS },
  waybill: {
    type: 'select',
    options: WAYBILL_OPTIONS,
    buildClause: (filter) => {
      const clause = WAYBILL_CLAUSE_BY_VALUE[filter?.value];
      return clause ? `(${clause})` : null;
    },
  },
};

const SEARCH_FIELDS = ['TransferNo'];

// ── biçimlendirme ────────────────────────────────────────────────────────────
export function fmtDateTime(v) {
  if (!v) return '-';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '-'
    : d.toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
export function fmtQty(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('tr-TR', { maximumFractionDigits: 2 });
}
/** "12 dk" / "6s 20dk" / "4 gün" — asılı kalma süresi kırmızı bandın ana metriğidir. */
export function fmtDuration(fromValue, toValue) {
  if (!fromValue) return '-';
  const from = new Date(fromValue).getTime();
  const to = toValue ? new Date(toValue).getTime() : Date.now();
  if (Number.isNaN(from) || Number.isNaN(to)) return '-';
  const mins = Math.max(0, Math.round((to - from) / 60000));
  if (mins < 60) return `${mins} dk`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}s ${mins % 60}dk`;
  return `${Math.floor(hours / 24)} gün`;
}

/** Transferin toplam farkı. OData satırı `totalDifference` KOLONUNU taşır (denormalize rollup). */
function differenceOf(t) {
  if (t.totalDifference != null) return Number(t.totalDifference) || 0;
  return Number(t.totalConfirmed || 0) - Number(t.totalDispatched || 0);
}
/** İrsaliye durumu — fromWaybillPrinted/toWaybillPrinted bayraklarından (§13.10: İKİSİ de şart). */
function waybillStateOf(t) {
  if (!WAYBILL_REQUIRED_STATUSES.has(t.status)) return 'na';
  return (t.fromWaybillPrinted && t.toWaybillPrinted) ? 'printed' : 'missing';
}

const st = {
  blk: { borderTop: '1px solid var(--border-color, rgba(15,23,42,0.10))', padding: '14px 16px' },
  blkDanger: { background: 'var(--danger-soft, rgba(226, 68, 92,0.07))' },
  blkTitle: {
    margin: '0 0 10px', fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
    color: 'var(--text-secondary, #6b7280)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
  },
  blkTitleDanger: { color: 'var(--danger, #e2445c)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '8px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    color: 'var(--text-secondary, #6b7280)', borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.10))',
  },
  td: {
    padding: '9px', whiteSpace: 'nowrap', color: 'var(--text-primary, #111827)',
    borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.06))',
  },
  num: { textAlign: 'right' },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 700 },
  empty: { padding: '14px 4px', fontSize: 13, color: 'var(--text-secondary, #6b7280)' },
  smallBtn: { ...s.btnSecondary, padding: '5px 9px', fontSize: 11, minWidth: 0, height: 'auto' },
  chip: {
    padding: '5px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: 'pointer',
    border: '1px solid var(--border-color, rgba(15,23,42,0.14))', background: 'var(--bg-card, #fff)',
    color: 'var(--text-secondary, #6b7280)', userSelect: 'none',
  },
  chipOn: {
    background: 'var(--accent, #0073ea)', color: '#fff', borderColor: 'transparent',
  },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  filterInput: { ...s.input, height: 34, fontSize: 12, borderRadius: 6, width: 150 },
  filterSelect: { ...s.select, height: 34, fontSize: 12, borderRadius: 6, width: 170 },
  lookup: { width: 200 },
  danger: { color: 'var(--danger, #e2445c)', fontWeight: 800 },
  muted: { color: 'var(--text-secondary, #6b7280)' },
};

/** Fark çözümleme modalı — GEREKÇE ZORUNLU (§9.1); boşken gönderilemez. */
export function ResolveModal({ row, vehicleLabel, onClose, onSubmit, busy }) {
  const [action, setAction] = useState('writeoff');
  const [reason, setReason] = useState('');
  const canSubmit = reason.trim().length > 0 && !busy;
  const meta = TRANSIT_ACTIONS.find((a) => a.value === action);

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={s.modalTitle}>Transit Farkını Çöz — {row.transferNo || '-'}</h3>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>
        <div style={s.modalBody}>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-secondary, #6b7280)' }}>
            <b style={{ color: 'var(--text-primary, #111827)' }}>{row.productName || row.productCode || '-'}</b>
            {' · '}<b style={st.danger}>{fmtQty(row.quantity)}</b> adet TRANSIT'te asılı
            {' · '}{vehicleLabel(row.fromWarehouseId)} → {vehicleLabel(row.toWarehouseId)}
          </p>
          <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>
            Çözüm transferin TRANSIT bakiyesinin tamamına uygulanır; bakiye sıfırlanır ve karşı ayak
            seçilen hedefe yazılır. Araç stoğu değişirse taşıma irsaliyesinin yeniden basılması gerekir.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {TRANSIT_ACTIONS.map((a) => (
              <label key={a.value} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', fontSize: 13 }}>
                <input type="radio" name="transit-action" value={a.value} checked={action === a.value}
                       onChange={() => setAction(a.value)} style={{ marginTop: 3 }} />
                <span>
                  <b>{a.label}</b>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary, #6b7280)' }}>{a.hint}</span>
                </span>
              </label>
            ))}
          </div>

          <label style={s.label}>Gerekçe *</label>
          <textarea
            style={{ ...s.input, minHeight: 72, resize: 'vertical', width: '100%' }}
            value={reason}
            placeholder="Neden bu karar verildi? (zorunlu — denetim izi)"
            onChange={(e) => setReason(e.target.value)}
          />
          {!reason.trim() && (
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger, #e2445c)' }}>
              Gerekçe zorunludur — gerekçesiz çözüm kaydedilemez.
            </div>
          )}
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnSecondary} onClick={onClose} disabled={busy}>Vazgeç</button>
          <button
            style={{ ...(action === 'writeoff' ? s.btnDanger : s.btnPrimary), opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
            disabled={!canSubmit}
            onClick={() => onSubmit({ transferId: row.transferId, action, reason: reason.trim() })}
          >
            {busy ? 'İşleniyor…' : (meta?.label || 'Uygula')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * focus='all'      → /vehicle-transfers  (üç blok)
 * focus='transit'  → /transit-balances   (yalnız TRANSIT + onay bekleyen; geçmiş yok)
 */
export function StockTransfersPage({ focus = 'all' }) {
  const transitOnly = focus === 'transit';
  const navigate = useNavigate();
  const { canEdit } = usePageActions();

  usePageMeta(
    transitOnly ? 'Transit Bakiye' : 'Araçlar Arası Transfer İzleme',
    transitOnly
      ? "TRANSIT'te asılı kalan bakiyeler ve fark çözümleme."
      : 'Araçlar arası transferler, transit farkları ve taşıma irsaliyesi takibi.',
  );

  const [balances, setBalances] = useState([]);
  const [pending, setPending] = useState([]);
  const [transitLoading, setTransitLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [resolveRow, setResolveRow] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [notifying, setNotifying] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  // TRANSIT/onay bloklarının satırlarındaki transferlerin bağlamı (cari + temsilciler).
  // REST ucu bunları taşımıyor; geçmiş sayfası artık SAYFALI olduğu için oradan okunamaz →
  // id'lerle hedefli TEK OData isteğiyle çözülür.
  const [ctxById, setCtxById] = useState(() => new Map());

  // İrsaliyesiz rozeti: SUNUCU gerçeği (sayfadan bağımsız) — sayfalanan listede
  // "bu sayfada irsaliyesiz yok" demek "irsaliyesiz yok" DEMEK DEĞİLDİR.
  const [missingWaybillCount, setMissingWaybillCount] = useState(0);

  // Lookup'lar: araç = Warehouse (TextId = plaka), temsilci adı kart üstünde gösterilir.
  const [warehouses, setWarehouses] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  const [vendors, setVendors] = useState([]);

  // Filtreler — toolbar (HEPSİ sunucuya iner: baseFilter)
  const [datePreset, setDatePreset] = useState('7');
  const [vendorId, setVendorId] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [productText, setProductText] = useState('');   // yalnız TRANSIT bloğuna (ürün kolonu listede yok)
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [onlyMissingWaybill, setOnlyMissingWaybill] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ── Taban filtre: toolbar seçimleri → OData $filter (kullanıcı hunileriyle VE'lenir) ──
  const baseFilter = useMemo(() => {
    const clauses = [];
    if (datePreset) {
      const since = new Date(Date.now() - Number(datePreset) * 86400000);
      clauses.push(`RequestedAt ge ${since.toISOString()}`);
    }
    if (vendorId) clauses.push(`VendorId eq ${vendorId}`);
    // Araç seçimi VEREN ya da ALAN olabilir (REST ucundaki `vehicleId` ile aynı semantik).
    if (vehicleId) clauses.push(`(FromWarehouseId eq ${vehicleId} or ToWarehouseId eq ${vehicleId})`);
    if (onlyDiff) clauses.push('HasDiscrepancy eq true');
    if (onlyMissingWaybill) clauses.push(MISSING_WAYBILL_CLAUSE);
    return clauses.map((c) => `(${c})`).join(' and ');
  }, [datePreset, vendorId, vehicleId, onlyDiff, onlyMissingWaybill]);

  const list = useC360List({
    apiBase: TRANSFERS_ODATA,
    baseFilter,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'RequestedAt',
    defaultSortDir: 'desc',
    ready: !transitOnly,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows: transfers, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/warehouses/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/representatives/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/vendors/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
    ]).then(([w, r, v]) => {
      setWarehouses(unwrapLookupItems(w));
      setRepresentatives(unwrapLookupItems(r));
      setVendors(unwrapLookupItems(v));
    }).catch(() => {});
  }, []);

  // ── ①② TRANSIT + onay bekleyen (REST — ledger gerçeği, sayfalanmaz) ───────
  const loadTransit = useCallback(async () => {
    setTransitLoading(true);
    try {
      const res = await fetch(`${TRANSIT_API}?pendingHours=4`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Transit bakiyeleri alınamadı');
      const body = await res.json();
      const data = body?.data ?? body;
      setBalances(data?.balances || []);
      setPending(data?.pendingConfirmation || []);
    } catch (e) {
      showToast('error', e.message || 'Transit bakiyeleri alınamadı');
    } finally {
      setTransitLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadTransit(); }, [loadTransit]);

  // İrsaliyesiz sayacı — sunucu sayımı ($count=true&$top=0).
  const loadMissingWaybillCount = useCallback(async () => {
    if (transitOnly) return;
    try {
      const url = `${TRANSFERS_ODATA}?$count=true&$top=0&$filter=${encodeURIComponent(MISSING_WAYBILL_CLAUSE)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) return;
      const c = getExplicitODataTotalCount(await res.json());
      if (c != null) setMissingWaybillCount(c);
    } catch { /* rozet kritik değil — liste yine çalışır */ }
  }, [transitOnly]);

  useEffect(() => { loadMissingWaybillCount(); }, [loadMissingWaybillCount]);

  // TRANSIT/onay bloklarındaki transferlerin bağlamı (cari + temsilciler) — hedefli tek istek.
  const contextIds = useMemo(() => {
    const ids = new Set();
    balances.forEach((b) => { if (b.transferId) ids.add(String(b.transferId)); });
    pending.forEach((p) => { if (p.id) ids.add(String(p.id)); });
    return [...ids].sort();
  }, [balances, pending]);
  const contextKey = contextIds.join(',');

  useEffect(() => {
    if (!contextKey) { setCtxById(new Map()); return; }
    const ids = contextKey.split(',');
    const filter = ids.map((id) => `Id eq ${id}`).join(' or ');
    const url = `${TRANSFERS_ODATA}?$top=${ids.length}&$select=${
      encodeURIComponent('Id,TransferNo,VendorId,FromRepresentativeId,ToRepresentativeId')
    }&$filter=${encodeURIComponent(filter)}`;
    let cancelled = false;
    fetch(url, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const map = new Map();
        (data.value || []).forEach((t) => map.set(String(t.id), t));
        setCtxById(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [contextKey]);

  const vehicleLabel = useCallback((id) => {
    if (!id) return '-';
    const w = warehouses.find((x) => x.id === id);
    if (!w) return '-';
    return w.textId || w.name || '-'; // TextId = plaka (gösterim)
  }, [warehouses]);

  const vehicleFull = useCallback((warehouseId, representativeId) => {
    const plate = vehicleLabel(warehouseId);
    const rep = representatives.find((x) => x.id === representativeId);
    return rep?.name ? `${plate} · ${rep.name}` : plate;
  }, [vehicleLabel, representatives]);

  const vendorName = useCallback((id) => {
    if (!id) return '';
    const v = vendors.find((x) => x.id === id);
    return v?.name || v?.textId || '';
  }, [vendors]);

  // Huni seçenekleri (Cari + Veren/Alan Araç) — lookup'tan; C360Table `resolveFilterProps` ile alır.
  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.id, label: v.name || v.textId || '-' })),
    [vendors],
  );
  const vehicleOptions = useMemo(
    () => warehouses.map((w) => ({ value: w.id, label: w.textId || w.name || '-' })),
    [warehouses],
  );
  const resolveFilterProps = useCallback((columnId, meta) => {
    if (columnId === 'vendor') return { options: vendorOptions };
    if (columnId === 'fromVehicle' || columnId === 'toVehicle') return { options: vehicleOptions };
    return { options: meta.options };
  }, [vendorOptions, vehicleOptions]);

  // Ürün filtresi: geçmiş listesi KALEM taşımıyor → yalnız TRANSIT bloğuna uygulanır
  // (orada ürün adı zaten satırın kendisindedir). Cari filtresi ctxById üzerinden çözülür.
  const filteredBalances = useMemo(() => {
    const pq = productText.trim().toLocaleLowerCase('tr');
    return balances.filter((b) => {
      if (vendorId) {
        const t = ctxById.get(String(b.transferId));
        if (t && String(t.vendorId) !== String(vendorId)) return false;
      }
      if (pq) {
        const hay = `${b.productName || ''} ${b.productCode || ''}`.toLocaleLowerCase('tr');
        if (!hay.includes(pq)) return false;
      }
      return true;
    });
  }, [balances, productText, vendorId, ctxById]);

  // ── Aksiyonlar ────────────────────────────────────────────────────────────
  const refreshAll = useCallback(() => {
    fetchData();
    loadTransit();
    loadMissingWaybillCount();
  }, [fetchData, loadTransit, loadMissingWaybillCount]);

  const submitResolve = async ({ transferId, action, reason }) => {
    setResolving(true);
    try {
      const res = await fetch(`${TRANSIT_API}/${transferId}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ action, reason }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Fark çözülemedi');
      const data = body?.data ?? body;
      showToast('success', data?.waybillReprintRequired
        ? 'Fark çözüldü — araç stoğu değişti, taşıma irsaliyesi yeniden basılmalı.'
        : 'Fark zayiata yazıldı.');
      setResolveRow(null);
      refreshAll();
    } catch (e) {
      showToast('error', e.message || 'Fark çözülemedi');
    } finally {
      setResolving(false);
    }
  };

  /** Temsilciye push hatırlatma (§9: backoffice irsaliyesiz aracı uyarabilmeli). */
  const sendReminder = useCallback(async (transfer, kind) => {
    const repIds = [transfer.fromRepresentativeId, transfer.toRepresentativeId].filter(Boolean);
    const targetIds = kind === 'confirm'
      ? [transfer.toRepresentativeId].filter(Boolean) // onayı ALAN yapar
      : repIds;                                        // irsaliyeyi İKİ araç da basar
    if (targetIds.length === 0) { showToast('error', 'Temsilci bağı yok — hatırlatma gönderilemedi.'); return; }
    setNotifying(transfer.id);
    try {
      const payload = kind === 'confirm'
        ? {
          type: 'STOCK_TRANSFER',
          title: 'Transfer onayı bekliyor',
          body: `${transfer.transferNo || 'Transfer'}: mal TRANSIT'te bekliyor, sayımı yapıp onaylayın.`,
          criteria: { representativeIds: targetIds },
        }
        : {
          type: 'STOCK_TRANSFER',
          title: 'Taşıma irsaliyesi basılmadı',
          body: `${transfer.transferNo || 'Transfer'}: araç stoğunuz değişti. Yola çıkmadan taşıma irsaliyesi basın.`,
          criteria: { representativeIds: targetIds },
        };
      const res = await fetch(NOTIFY_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Hatırlatma gönderilemedi');
      showToast('success', 'Hatırlatma temsilciye gönderildi.');
    } catch (e) {
      showToast('error', e.message || 'Hatırlatma gönderilemedi');
    } finally {
      setNotifying(null);
    }
  }, [showToast]);

  // ── ③ Transfer geçmişi tablosu ────────────────────────────────────────────
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('transferNo', {
      header: 'Transfer No', meta: { filterType: 'text' },
      cell: (info) => <span className="c360-erp-chip">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('requestedAt', {
      header: 'Tarih', enableColumnFilter: false,
      cell: (info) => fmtDateTime(info.getValue()),
    }),
    columnHelper.display({
      id: 'fromVehicle', header: 'Veren Araç', enableSorting: false, meta: { filterType: 'select' },
      cell: ({ row }) => vehicleFull(row.original.fromWarehouseId, row.original.fromRepresentativeId),
    }),
    columnHelper.display({
      id: 'toVehicle', header: 'Alan Araç', enableSorting: false, meta: { filterType: 'select' },
      cell: ({ row }) => vehicleFull(row.original.toWarehouseId, row.original.toRepresentativeId),
    }),
    columnHelper.display({
      id: 'vendor', header: 'Cari', enableSorting: false, meta: { filterType: 'select' },
      cell: ({ row }) => vendorName(row.original.vendorId) || '-',
    }),
    columnHelper.accessor('lineCount', {
      header: 'Kalem', meta: { filterType: 'number' },
      cell: (info) => <span style={st.num}>{info.getValue() ?? 0}</span>,
    }),
    columnHelper.accessor('totalDispatched', {
      header: 'Gönderilen', enableColumnFilter: false,
      cell: (info) => (WAYBILL_REQUIRED_STATUSES.has(info.row.original.status) ? fmtQty(info.getValue()) : '—'),
    }),
    columnHelper.accessor('totalConfirmed', {
      header: 'Onaylanan', enableColumnFilter: false,
      cell: (info) => {
        const t = info.row.original;
        return (t.status === 'confirmed' || t.status === 'confirmed_with_diff') ? fmtQty(info.getValue()) : '—';
      },
    }),
    columnHelper.display({
      id: 'difference', header: 'Fark', enableSorting: false, meta: { filterType: 'select' },
      cell: ({ row }) => {
        const t = row.original;
        if (t.status !== 'confirmed' && t.status !== 'confirmed_with_diff') return '—';
        const diff = differenceOf(t);
        if (!diff) return <span style={st.muted}>0</span>;
        // Fark TRANSIT'te asılı kaldı — bu satır backoffice'in çözmesini bekliyor.
        return <span style={st.danger}>{diff > 0 ? `+${fmtQty(diff)}` : fmtQty(diff)}</span>;
      },
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => <StatusBadge tone={statusTone(info.getValue())}>{statusLabel(info.getValue())}</StatusBadge>,
    }),
    columnHelper.display({
      id: 'waybill', header: 'İrsaliye', enableSorting: false, meta: { filterType: 'select' },
      cell: ({ row }) => {
        const t = row.original;
        // Basılmamış taşıma irsaliyesi = yolda BELGESİZ mal taşıyan araç (§9).
        // Bayraklar StockTransfer KOLONUDUR — satır başına detay çağrısı YOK, huni SUNUCUDA süzer.
        const state = waybillStateOf(t);
        if (state === 'na') return <span style={st.muted}>—</span>;
        const ok = state === 'printed';
        return (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <StatusBadge tone={ok ? 'ok' : 'bad'}>{ok ? 'Basıldı' : 'Basılmadı'}</StatusBadge>
            {!ok && canEdit && (
              <button
                type="button" style={st.smallBtn} disabled={notifying === t.id}
                title="Temsilciye irsaliye hatırlatması gönder"
                onClick={(e) => { e.stopPropagation(); sendReminder(t, 'waybill'); }}
              >
                {notifying === t.id ? '…' : 'Hatırlat'}
              </button>
            )}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'duration', header: 'Süre', enableSorting: false,
      cell: ({ row }) => {
        const t = row.original;
        const end = t.confirmedAt || (t.status === 'dispatched' ? null : t.updatedDate);
        return fmtDuration(t.requestedAt, end);
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false, enableSorting: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view"
                  title="Görüntüle" aria-label="Görüntüle"
                  onClick={(e) => { e.stopPropagation(); navigate(`/vehicle-transfers/${row.original.id}`); }}>
            <ViewIcon />
          </button>
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [vehicleFull, vendorName, notifying, canEdit, navigate, sendReminder]);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('RequestedAt', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const table = useReactTable({
    data: transfers,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  // Dışa aktarım: FİLTRELİ TÜM kayıtlar (görünen sayfa değil) — sunucudan iner.
  const handleExport = async () => {
    const cols = [
      { headerName: 'Transfer No', valueGetter: ({ row }) => row.transferNo || '' },
      { headerName: 'Tarih', valueGetter: ({ row }) => fmtDateTime(row.requestedAt) },
      { headerName: 'Veren Araç', valueGetter: ({ row }) => vehicleFull(row.fromWarehouseId, row.fromRepresentativeId) },
      { headerName: 'Alan Araç', valueGetter: ({ row }) => vehicleFull(row.toWarehouseId, row.toRepresentativeId) },
      { headerName: 'Cari', valueGetter: ({ row }) => vendorName(row.vendorId) },
      { headerName: 'Kalem', valueGetter: ({ row }) => row.lineCount ?? 0 },
      { headerName: 'Gönderilen', valueGetter: ({ row }) => row.totalDispatched ?? 0 },
      { headerName: 'Onaylanan', valueGetter: ({ row }) => row.totalConfirmed ?? 0 },
      { headerName: 'Fark', valueGetter: ({ row }) => differenceOf(row) },
      { headerName: 'Durum', valueGetter: ({ row }) => statusLabel(row.status) },
      {
        headerName: 'İrsaliye',
        valueGetter: ({ row }) => {
          const state = waybillStateOf(row);
          if (state === 'na') return '-';
          return state === 'printed' ? 'Basıldı' : 'Basılmadı';
        },
      },
      { headerName: 'Süre', valueGetter: ({ row }) => fmtDuration(row.requestedAt, row.confirmedAt) },
    ];
    try {
      const n = await exportRows(cols, `arac-transferleri_${new Date().toISOString().slice(0, 10)}`);
      showToast(n ? 'success' : 'error', n ? `${n} kayıt dışa aktarıldı` : 'Dışa aktarılacak veri yok');
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarım başarısız');
    }
  };

  const toolbarNode = (
    <div style={st.filters}>
      <select style={st.filterSelect} value={datePreset} onChange={(e) => { setDatePreset(e.target.value); setCurrentPage(1); }}>
        {DATE_PRESETS.map((d) => <option key={d.value} value={d.value}>Tarih: {d.label}</option>)}
      </select>

      {/* Lookup'lar ham <select> değil, projenin ilişki seçicisi (datagrid + arama + filtre). */}
      <span style={st.lookup}>
        <RelationPicker
          config={RELATION_CONFIGS.vendor}
          rows={vendors}
          value={vendorId}
          selectedLabel={vendorName(vendorId)}
          onChange={(val) => { setVendorId(val || ''); setCurrentPage(1); }}
          placeholder="Cari: Tümü"
        />
      </span>

      {!transitOnly && (
        <span style={st.lookup}>
          <RelationPicker
            config={RELATION_CONFIGS.warehouse}
            rows={warehouses}
            value={vehicleId}
            selectedLabel={vehicleId ? vehicleLabel(vehicleId) : ''}
            onChange={(val) => { setVehicleId(val || ''); setCurrentPage(1); }}
            placeholder="Araç: Tümü"
          />
        </span>
      )}

      <input style={st.filterInput} placeholder="Ürün (transit)" value={productText}
             onChange={(e) => setProductText(e.target.value)} title="Ürün filtresi TRANSIT bloğuna uygulanır" />

      {!transitOnly && (
        <>
          <input
            style={st.filterInput}
            placeholder="Transfer no ile ara..."
            value={filterInput}
            onChange={(e) => setFilterInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
          />
          {filterText && (
            <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>
          )}
          {/* İki çip de SUNUCU filtresidir (HasDiscrepancy / irsaliye bayrakları). */}
          <span style={{ ...st.chip, ...(onlyDiff ? st.chipOn : null) }} role="button"
                onClick={() => { setOnlyDiff((v) => !v); setCurrentPage(1); }}>
            Sadece farklı olanlar
          </span>
          <span style={{ ...st.chip, ...(onlyMissingWaybill ? st.chipOn : null) }} role="button"
                onClick={() => { setOnlyMissingWaybill((v) => !v); setCurrentPage(1); }}>
            İrsaliyesiz
          </span>
        </>
      )}
      <button style={s.btnSecondary} onClick={refreshAll}>Yenile</button>
      {!transitOnly && <ColumnChooser table={table} />}
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
        <button type="button" title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}
                style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <ExcelIcon size={16} />
        </button>
      </span>
    </div>
  );

  const metaNode = (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <StatusBadge tone={filteredBalances.length ? 'bad' : 'ok'}>
        {filteredBalances.length} transitte asılı
      </StatusBadge>
      {!transitOnly && (
        <StatusBadge tone={missingWaybillCount ? 'wait' : 'ok'}>{missingWaybillCount} irsaliyesiz</StatusBadge>
      )}
    </span>
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title={transitOnly ? 'Transit Bakiye' : 'Araçlar Arası Transfer İzleme'}
        subtitle={transitOnly
          ? "TRANSIT'te asılı her koli, birinin elinde olan ama hiçbir aracın stoğunda görünmeyen maldır."
          : 'Transit farkları, onay bekleyen transferler ve taşıma irsaliyesi takibi.'}
        meta={metaNode}
        toolbar={toolbarNode}
        footer={transitOnly ? null : (
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={pageSize}
            rowCount={transfers.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      >
        {/* ① TRANSIT'TE ASILI BAKİYE — en üstte, kırmızı. */}
        <div style={{ ...st.blk, ...st.blkDanger, borderTop: 'none' }}>
          <p style={{ ...st.blkTitle, ...st.blkTitleDanger }}>
            ⚠ Transit'te asılı bakiye — çözüm bekliyor
            {filteredBalances.length > 0 && <StatusBadge tone="bad">{filteredBalances.length}</StatusBadge>}
          </p>
          {transitLoading && filteredBalances.length === 0 ? (
            <div style={st.empty}>Yükleniyor…</div>
          ) : filteredBalances.length === 0 ? (
            <div style={st.empty}>Transit'te asılı bakiye yok — tüm transferler mutabık.</div>
          ) : (
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={st.th}>Ürün</th>
                  <th style={{ ...st.th, ...st.num }}>Miktar</th>
                  <th style={st.th}>Transfer</th>
                  <th style={st.th}>Veren → Alan</th>
                  <th style={st.th}>Durum</th>
                  <th style={st.th}>Süre</th>
                  <th style={{ ...st.th, textAlign: 'right' }}>Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {filteredBalances.map((b) => (
                  <tr key={`${b.transferId}-${b.productId}-${b.stockType}`}>
                    <td style={st.td}>{b.productName || b.productCode || '-'}</td>
                    <td style={{ ...st.td, ...st.num, ...st.danger }}>{fmtQty(b.quantity)}</td>
                    <td style={{ ...st.td, ...st.mono, cursor: 'pointer' }}
                        onClick={() => navigate(`/vehicle-transfers/${b.transferId}`)}>
                      {b.transferNo || '-'}
                    </td>
                    <td style={st.td}>{b.fromWarehouseName || vehicleLabel(b.fromWarehouseId)} → {b.toWarehouseName || vehicleLabel(b.toWarehouseId)}</td>
                    <td style={st.td}><StatusBadge tone={statusTone(b.status)}>{statusLabel(b.status)}</StatusBadge></td>
                    <td style={{ ...st.td, ...(Number(b.hangingDays) >= 1 ? st.danger : null) }}>
                      {b.hangingDays != null ? (b.hangingDays >= 1 ? `${b.hangingDays} gün` : fmtDuration(b.hangingSince)) : '-'}
                    </td>
                    <td style={{ ...st.td, textAlign: 'right' }}>
                      {b.resolutionAction ? (
                        <StatusBadge tone="ok">Çözüldü</StatusBadge>
                      ) : canEdit ? (
                        <span style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button type="button" style={{ ...st.smallBtn, color: 'var(--danger, #e2445c)', borderColor: 'var(--danger, #e2445c)' }}
                                  onClick={() => setResolveRow(b)}>Zayiat</button>
                          <button type="button" style={st.smallBtn} onClick={() => setResolveRow(b)}>Verene iade</button>
                          <button type="button" style={st.smallBtn} onClick={() => setResolveRow(b)}>Alana ekle</button>
                        </span>
                      ) : <span style={st.muted}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ② ONAY BEKLEYEN TRANSFERLER — dispatched + 4 saati aşanlar. */}
        <div style={st.blk}>
          <p style={st.blkTitle}>
            ⏱ Onay bekleyen transferler — 4 saati aşanlar
            {pending.length > 0 && <StatusBadge tone="wait">{pending.length}</StatusBadge>}
          </p>
          {pending.length === 0 ? (
            <div style={st.empty}>4 saati aşan onay bekleyen transfer yok.</div>
          ) : (
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={st.th}>Transfer</th>
                  <th style={st.th}>Veren</th>
                  <th style={st.th}>Alan</th>
                  <th style={{ ...st.th, ...st.num }}>Kalem</th>
                  <th style={st.th}>Durum</th>
                  <th style={st.th}>Bekleme</th>
                  <th style={{ ...st.th, textAlign: 'right' }}>Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((p) => {
                  // Temsilci bağı REST ucunda yok → ctxById (hedefli OData isteği) çözer.
                  const t = { ...p, ...(ctxById.get(String(p.id)) || {}) };
                  return (
                    <tr key={p.id}>
                      <td style={{ ...st.td, ...st.mono, cursor: 'pointer' }} onClick={() => navigate(`/vehicle-transfers/${p.id}`)}>
                        {p.transferNo || '-'}
                      </td>
                      <td style={st.td}>{vehicleFull(p.fromWarehouseId, t.fromRepresentativeId)}</td>
                      <td style={st.td}>{vehicleFull(p.toWarehouseId, t.toRepresentativeId)}</td>
                      <td style={{ ...st.td, ...st.num }}>{p.lineCount ?? 0}</td>
                      <td style={st.td}><StatusBadge tone="wait">Transit</StatusBadge></td>
                      <td style={{ ...st.td, ...st.danger }}>{fmtDuration(p.dispatchedAt)}</td>
                      <td style={{ ...st.td, textAlign: 'right' }}>
                        {canEdit && (
                          <button type="button" style={st.smallBtn} disabled={notifying === p.id}
                                  title="Alan temsilciye onay hatırlatması gönder"
                                  onClick={() => sendReminder(t, 'confirm')}>
                            {notifying === p.id ? '…' : 'Hatırlat'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ③ TRANSFER GEÇMİŞİ — c360 grid: SUNUCU sayfalama + sıralama + filtre (useC360List) */}
        {!transitOnly && (
          <div style={st.blk}>
            <p style={st.blkTitle}>🕘 Transfer geçmişi · {totalCount} kayıt</p>
            <C360Table
              table={table}
              columnFilterMeta={COLUMN_FILTER_META}
              columnFilters={columnFilters}
              onApplyFilter={setColumnFilter}
              onClearFilter={clearColumnFilter}
              resolveFilterProps={resolveFilterProps}
              loading={loading}
              onRowDoubleClick={(row) => navigate(`/vehicle-transfers/${row.id}`)}
              emptyText="Transfer bulunamadı"
            />
          </div>
        )}
      </ListPageShell>

      {resolveRow && (
        <ResolveModal
          row={resolveRow}
          vehicleLabel={vehicleLabel}
          busy={resolving}
          onClose={() => setResolveRow(null)}
          onSubmit={submitResolve}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default StockTransfersPage;
